import React from "react";
import { DateRangePicker } from "@components";
import { useController } from "./useController";

interface DateRange {
    startDate: string | null;
    endDate: string | null;
}

interface LazyLoadingListProps<T = any> {
    fetchItems: (page: number) => Promise<T[]>;
    renderItem: (item: T, itemArr: T[]) => React.ReactNode;
    initialItems?: T[];
    itemsPerPage?: number;
    hasMore?: boolean;
    loadingComponent?: React.ReactNode;
    endOfListComponent?: React.ReactNode;
    onError?: (error: Error) => void;
    threshold?: number;
    withDateFilter?: boolean;
    filterByDate?: (items: T[], dateRange: DateRange) => T[];
}

export const LazyLoadingList = <T,>({
    fetchItems,
    renderItem,
    initialItems = [],
    itemsPerPage = 10,
    hasMore = true,
    loadingComponent = null,
    endOfListComponent = null,
    onError,
    threshold = 100,
    withDateFilter = false,
    filterByDate,
}: LazyLoadingListProps<T>) => {
    const {
        listRef,
        observerRef,
        dateRange,
        isLoading,
        hasMoreState,
        filteredItems,
        handleDateChange
    } = useController<T>({
        initialItems,
        hasMore,
        threshold,
        withDateFilter,
        onError,
        fetchItems,
        filterByDate,

    })
    return (
        <div className="space-y-4" ref={listRef} style={{ overflowY: "auto", maxHeight: "50vh" }}>
            {withDateFilter && (
                <div className="top-0 sm:sticky -top-7 bg-gray-100 z-10 shadow-md rounded-lg">
                    <DateRangePicker
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        onStartDateChange={(date) => handleDateChange(date, dateRange.endDate)}
                        onEndDateChange={(date) => handleDateChange(dateRange.startDate, date)}
                        onSearch={() => handleDateChange(dateRange.startDate, dateRange.endDate)}
                        buttonLabel="Filtrer"
                    />
                </div>
            )}

            {filteredItems.map((item, index) => (
                <div key={index} className="card shadow-lg bg-base-100 p-4">
                    {renderItem(item, filteredItems)}
                </div>
            ))}

            {isLoading && (
                <div className="flex justify-center">
                    {loadingComponent || (
                        <span className="loading loading-spinner loading-lg text-primary"></span>
                    )}
                </div>
            )}

            {!isLoading && filteredItems.length === 0 && (
                <div className="text-center text-gray-500">
                    Aucun élément ne correspond à vos critères de recherche.
                </div>
            )}

            {!isLoading && !hasMoreState && filteredItems.length > 0 && (
                <div className="text-center text-gray-500">
                    {endOfListComponent || "Fin de la liste."}
                </div>
            )}

            {hasMoreState && <div ref={observerRef} style={{ height: "1px" }} />}
        </div>
    );
};